import { clsx } from '@alexis/helpers/clsx';
import { appendSearchParams } from '@alexis/helpers/searchParam';
import { translateText } from '@alexis/helpers/translation';
import React, { forwardRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import Button from '@components/Button';

import { getGuestsCount } from '@helpers/hotel';
import { hotelsRoute } from '@helpers/routeTranslation';

import ChevronDownSmallIcon from '@icons/chevron_down_small.svg';

import { Currency } from '@wegoTypes/currency';
import type { CurrentSite } from '@wegoTypes/currentSite';
import type {
  HotelBookingRoomTypes,
  HotelBookingRoomTypesDataRoom,
} from '@wegoTypes/hotelBooking/hotelRoomTypes';
import type { HotelMetaSearch } from '@wegoTypes/hotels/hotelMetaSearch';
import { HotelRateAmenity } from '@wegoTypes/hotels/metasearch/hotelRateAmenity';
import type { MetasearchHotelRate } from '@wegoTypes/hotels/metasearch/metasearchHotelRate';
import type { Translations } from '@wegoTypes/translations';

import RoomDetail from '../RoomDetail';
import styles from './ChooseYourRoom.module.scss';

interface ChooseYourRoomProps {
  currency: Currency;
  currentSite: CurrentSite;
  exchangeRate: ExchangeRate;
  hotelBookingRoomTypes: HotelBookingRoomTypes;
  hotelMetaSearch: HotelMetaSearch;
  locale: string;
  localeParam: string | undefined;
  priceOptions: PriceOptions;
  rateAmenities: Array<HotelRateAmenity>;
  rates: Array<MetasearchHotelRate>;
  searchIdParam: string | null;
  searchWithoutExtraParams: string;
  selectedRateAmenityIds: Array<number>;
  translations: Translations;
}

const initialRoomsShown = 3;

const ChooseYourRoom = forwardRef<HTMLDivElement, ChooseYourRoomProps>(
  (
    {
      currency,
      currentSite,
      exchangeRate,
      hotelBookingRoomTypes,
      hotelMetaSearch,
      locale,
      localeParam,
      priceOptions,
      rateAmenities,
      rates,
      searchIdParam,
      searchWithoutExtraParams,
      selectedRateAmenityIds,
      translations,
    },
    ref,
  ) => {
    const { state } = useLocation();

    const [sortedRoomTypesWithRates, setSortedRoomTypesWithRates] = useState<
      Array<{
        rates: Array<MetasearchHotelRate>;
        roomType: HotelBookingRoomTypesDataRoom;
      }>
    >([]);
    const [showMoreRooms, setShowMoreRooms] = useState(false);

    useEffect(() => {
      const roomTypeMapped = new Map(
        hotelBookingRoomTypes.data.rooms.map((room) => [room.typeId, room]),
      );

      const groupedRatesByRoomTypeIds = rates
        .filter((rate) => rate.provider.directBooking)
        .reduce(
          (
            accumulator: Array<{
              rates: Array<MetasearchHotelRate>;
              roomType: HotelBookingRoomTypesDataRoom;
            }>,
            rate: MetasearchHotelRate,
          ) => {
            const roomType = roomTypeMapped.get(rate.params.room_type_id);

            if (!roomType) return accumulator;

            const roomTypeGroupIndex = accumulator.findIndex(
              (value) => value.roomType.typeId === rate.params.room_type_id,
            );

            if (roomTypeGroupIndex !== -1) {
              const roomTypeGroup = accumulator[roomTypeGroupIndex];

              roomTypeGroup.rates.push(rate);
            } else {
              const roomTypeGroup = { rates: [rate], roomType };

              accumulator.push(roomTypeGroup);
            }

            return accumulator;
          },
          [],
        );

      if (groupedRatesByRoomTypeIds.length > 0) {
        const slicedAndSortedRoomRates = groupedRatesByRoomTypeIds
          .slice(1)
          .sort((groupedRatesA, groupedRatesB) => {
            const hasRoomTypeImagesA = groupedRatesA.roomType.images.length > 0;
            const hasRoomTypeImagesB = groupedRatesB.roomType.images.length > 0;
            if (hasRoomTypeImagesA && hasRoomTypeImagesB) {
              return 0; // Keep the order
            } else if (hasRoomTypeImagesA) {
              return -1; // Move hasRoomTypeImagesA to the front
            } else if (hasRoomTypeImagesB) {
              return 1; // Move hasRoomTypeImagesB to the back
            } else {
              return 0;
            }
          });
        setSortedRoomTypesWithRates([groupedRatesByRoomTypeIds[0], ...slicedAndSortedRoomRates]);
      } else {
        setSortedRoomTypesWithRates([]);
      }
    }, [rates]);

    const getRateHandoffHref = (rate: MetasearchHotelRate) => {
      return appendSearchParams(
        `${hotelsRoute(currentSite, localeParam)}/booking/checkout${searchWithoutExtraParams}`,
        { search_id: searchIdParam, reference_id: rate.id },
      );
    };

    return (
      <div
        className={clsx(styles.container, sortedRoomTypesWithRates.length === 0 && styles.hide)}
        ref={ref}
      >
        <div className={styles.header}>
          <h3 className={styles.headerTitle}>{translations.choose_your_room}</h3>

          <div className={styles.providedByWego}>
            {translations.provided_by_partner}
            <Link
              aria-label='Wego'
              className={styles.logoLink}
              state={state}
              to={{ pathname: `/` }}
            />
          </div>
        </div>

        <div className={styles.roomDetails}>
          {sortedRoomTypesWithRates
            ?.slice(0, showMoreRooms ? undefined : initialRoomsShown)
            .map((sortedRoomTypesWithRate) =>
              sortedRoomTypesWithRate.rates.length > 0 ? (
                <RoomDetail
                  currency={currency}
                  exchangeRate={exchangeRate}
                  guestCount={
                    hotelMetaSearch.search ? getGuestsCount(hotelMetaSearch.search.rooms!) : 0
                  }
                  key={sortedRoomTypesWithRate.roomType.typeId}
                  locale={locale}
                  priceOptions={priceOptions}
                  rateAmenities={rateAmenities}
                  rateHandoffHref={getRateHandoffHref}
                  roomTypeWithRate={sortedRoomTypesWithRate}
                  selectedRateAmenityIds={selectedRateAmenityIds}
                  translations={translations}
                />
              ) : null,
            )}

          {sortedRoomTypesWithRates.length > initialRoomsShown ? (
            <div className={styles.showMoreContainer}>
              <Button
                variant='outline'
                borderWidth='thick'
                iconPlacement='trailing'
                icon={
                  <ChevronDownSmallIcon
                    className={clsx(styles.chevronDown, showMoreRooms && styles.chevronUp)}
                  />
                }
                onClick={() => setShowMoreRooms((prev) => !prev)}
              >
                {showMoreRooms
                  ? translations.hide_rooms
                  : translateText(
                      translations.show_more_rooms_x,
                      locale,
                      sortedRoomTypesWithRates.length - initialRoomsShown,
                    )}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

export default ChooseYourRoom;
